import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { isEmpty, pickBy } from '../../util/common';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import {
  customerSignedDocumentDetail,
  customerSignedDocuments,
  inspectionSigningStatus,
  markMergeFailureMessagePosted,
  mergeFailureFields,
  providerSignedDocumentDetail,
  providerSignedDocuments,
  saveMergeFailureOverrides,
  sendForSigning,
  startInspectionSigning,
  transactionLineItems,
  transitionPrivileged,
} from '../../util/api';
import * as log from '../../util/log';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import {
  resolveLatestProcessName,
  getProcess,
  isBookingProcess,
  isNegotiationProcess,
} from '../../transactions/transaction';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;
      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then(response => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch(e => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};

// ================ Async Thunks ================ //

////////////////////
// fetchTimeSlots //
////////////////////
const fetchTimeSlotsPayloadCreator = (
  { listingId, start, end, timeZone, options },
  { rejectWithValue, extra: sdk, getState }
) => {
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().TransactionPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve({ timeSlots: dateData?.timeSlots || [], dateId, type: 'date' });
    }

    return sdk.timeslots
      .query({ listingId, start, end, ...extraParams })
      .then(response => {
        const timeSlots = denormalisedResponseEntities(response);
        return { timeSlots, dateId, type: 'date' };
      })
      .catch(e => {
        return rejectWithValue({ error: storableError(e), dateId, type: 'date' });
      });
  } else {
    const monthId = monthIdString(start, timeZone);
    return sdk.timeslots
      .query({ listingId, start, end, ...extraParams })
      .then(response => {
        const timeSlots = denormalisedResponseEntities(response);
        return { timeSlots, monthId, type: 'month' };
      })
      .catch(e => {
        return rejectWithValue({ error: storableError(e), monthId, type: 'month' });
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'TransactionPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);

// Backward compatible wrapper for fetchTimeSlots
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => dispatch => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options }));
};

//////////////////////
// fetchTransaction //
//////////////////////

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(
        fetchTimeSlotsThunk({
          listingId: listing.id,
          start: nextBoundary,
          end: nextMonthEnd,
          timeZone: tz,
          options: options(startOfToday),
        })
      ),
      dispatch(
        fetchTimeSlotsThunk({
          listingId: listing.id,
          start: nextMonth,
          end: followingMonthEnd,
          timeZone: tz,
          options: options(nextMonth),
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// fetchTransaction
const fetchTransactionPayloadCreator = (
  { id, txRole, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const listingRelationship = txResponse => {
    return txResponse.data.data.relationships.listing.data;
  };

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.currentStock',
          'listing.images',
          'listing.author',
          'listing.author.profileImage',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then(response => {
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];
      const transaction = denormalised[1];

      const processName = resolveLatestProcessName(transaction.attributes.processName);

      try {
        const process = getProcess(processName);
        const isInquiry = process.getState(transaction) === process.states.INQUIRY;

        // Fetch time slots for transactions that are in inquired state
        const canFetchTimeslots =
          txRole === 'customer' && isBookingProcess(processName) && isInquiry;

        if (canFetchTimeslots) {
          fetchMonthlyTimeSlots(dispatch, listing);
        }
      } catch (error) {
        console.log(`transaction process (${processName}) was not recognized`);
      }

      return response;
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionThunk = createAsyncThunk(
  'TransactionPage/fetchTransaction',
  fetchTransactionPayloadCreator
);

// Backward compatible wrapper for fetchTransaction
export const fetchTransaction = (id, txRole, config) => dispatch => {
  return dispatch(fetchTransactionThunk({ id, txRole, config }));
};

/**
 * Fetch the current customer's other transactions whose bookings overlap a date window.
 * Used by Phase 4c's pre-modal warning popup. Returns transactions in active states only
 * (accepted, modification-pending, delivered, pending-payment). Filters by date client-side
 * because the SDK's start/end query params are ignored. Excludes the current transaction
 * (the one being modified).
 *
 * @param {Object} params
 * @param {String} params.excludeTxId UUID string of the current transaction to exclude
 * @param {Number} params.windowStartMs Window start in ms (typically bookingStart - 1 day)
 * @param {Number} params.windowEndMs Window end in ms (typically bookingEnd + 1 day)
 * @param {Number} params.bookingStartMs Original booking start in ms (for same-day classification)
 * @param {Number} params.bookingEndMs Original booking end in ms (for same-day classification)
 * @returns {Promise<Array>} Array of normalized booking-summary objects:
 *   { txId, listingTitle, providerName, bookingStartMs, bookingEndMs, isSameDay, isAdjacentDay }
 *   where isSameDay/isAdjacentDay classify overlap relative to the original booking dates.
 */
export const fetchCustomerOverlappingBookings = ({
  excludeTxId,
  windowStartMs,
  windowEndMs,
  bookingStartMs,
  bookingEndMs,
}) => (dispatch, getState, sdk) => {
  return sdk.transactions
    .query({
      only: 'order',
      states: [
        'state/accepted',
        'state/modification-pending',
        'state/delivered',
        'state/pending-payment',
      ],
      include: ['booking', 'listing', 'provider'],
    })
    .then(response => {
      const txs = response?.data?.data || [];
      const included = response?.data?.included || [];

      // Build lookup maps for included resources
      const bookingsById = {};
      included
        .filter(i => i.type === 'booking')
        .forEach(b => {
          bookingsById[(b.id?.uuid)] = b;
        });
      const listingsById = {};
      included
        .filter(i => i.type === 'listing')
        .forEach(l => {
          listingsById[(l.id?.uuid)] = l;
        });
      const providersById = {};
      included
        .filter(i => i.type === 'user')
        .forEach(u => {
          providersById[(u.id?.uuid)] = u;
        });

      // Compute the same-day window from the original booking's calendar day(s)
      const bookingDayStart = new Date(bookingStartMs);
      bookingDayStart.setHours(0, 0, 0, 0);
      const bookingDayEnd = new Date(bookingEndMs);
      bookingDayEnd.setHours(23, 59, 59, 999);
      const bookingDayStartMs = bookingDayStart.getTime();
      const bookingDayEndMs = bookingDayEnd.getTime();

      // Filter and normalize
      return txs
        .filter(tx => tx.id?.uuid !== excludeTxId) // exclude current transaction
        .map(tx => {
          const bookingRef = tx.relationships?.booking?.data;
          const booking = bookingRef ? bookingsById[(bookingRef.id?.uuid)] : null;
          if (!booking) return null;

          const otherStartMs = booking.attributes?.start
            ? new Date(booking.attributes.start).getTime()
            : null;
          const otherEndMs = booking.attributes?.end
            ? new Date(booking.attributes.end).getTime()
            : null;
          if (otherStartMs == null || otherEndMs == null) return null;

          // Filter to ±1 day overlap window
          // Overlap exists if booking.start < windowEnd AND booking.end > windowStart
          const overlapsWindow = otherStartMs < windowEndMs && otherEndMs > windowStartMs;
          if (!overlapsWindow) return null;

          // Classify same-day vs adjacent-day
          // Same-day: the other booking's window intersects the original booking's calendar day(s)
          const isSameDay = otherStartMs <= bookingDayEndMs && otherEndMs >= bookingDayStartMs;
          const isAdjacentDay = !isSameDay; // anything inside windowStart/windowEnd but outside same-day

          const listingRef = tx.relationships?.listing?.data;
          const providerRef = tx.relationships?.provider?.data;
          const listing = listingRef ? listingsById[(listingRef.id?.uuid)] : null;
          const provider = providerRef ? providersById[(providerRef.id?.uuid)] : null;

          return {
            txId: tx.id?.uuid,
            listingTitle: listing?.attributes?.title || 'Booking',
            providerName: provider?.attributes?.profile?.displayName || 'Provider',
            bookingStartMs: otherStartMs,
            bookingEndMs: otherEndMs,
            isSameDay,
            isAdjacentDay,
          };
        })
        .filter(Boolean);
    })
    .catch(err => {
      // Silent fallthrough per spec — popup hides on query failure, modal opens directly
      // eslint-disable-next-line no-console
      console.warn(
        '[fetchCustomerOverlappingBookings] query failed, falling through silently:',
        err?.message
      );
      return [];
    });
};

////////////////////
// makeTransition //
////////////////////
const makeTransitionPayloadCreator = (
  { txId, transitionName, params },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  const transaction = getState()?.marketplaceData?.entities?.transaction?.[txId?.uuid];
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  const process = getProcess(processName);

  // This calls the client app's server to make a privileged transition.
  const privilegedTransition = () =>
    transitionPrivileged({
      isSpeculative: false,
      orderData: params?.orderData || {},
      bodyParams: {
        id: txId,
        transition: transitionName,
        params: {}, // NOTE: lineItems and metadata are included on the server-side.
      },
      queryParams: {
        expand: true,
      },
    });
  const normalTransition = () =>
    sdk.transactions.transition({ id: txId, transition: transitionName, params }, { expand: true });
  // Negotiation process / accept update: check if payin total has changed
  const acceptUpdateTransition = () =>
    sdk.transactions.show({ id: txId }, { expand: true }).then(response => {
      const updatedTransaction = response.data.data;
      const newPayinTotal = updatedTransaction.attributes.payinTotal.amount;
      const oldPayinTotal = transaction.attributes.payinTotal.amount;

      if (newPayinTotal !== oldPayinTotal) {
        dispatch(addMarketplaceEntities(response));
        throw new Error('Payin total has changed');
      }
      return sdk.transactions.transition(
        { id: txId, transition: transitionName, params },
        { expand: true }
      );
    });

  const makeCall = process?.isPrivileged(transitionName)
    ? privilegedTransition
    : isNegotiationProcess(processName) && transitionName === process.transitions.ACCEPT_UPDATE
    ? acceptUpdateTransition
    : normalTransition;

  return makeCall()
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchCurrentUserNotifications());

      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      if (e.message !== 'Payin total has changed') {
        log.error(e, `${transitionName}-failed`, {
          txId,
          transition: transitionName,
        });
      }
      return rejectWithValue(storableError(e));
    });
};

export const makeTransitionThunk = createAsyncThunk(
  'TransactionPage/makeTransition',
  makeTransitionPayloadCreator,
  {
    condition: ({ txId, transitionName, params }, { getState }) => {
      const state = getState();
      if (state.TransactionPage.transitionInProgress) {
        return false; // Don't execute the thunk if transition is already in progress
      }
      return true; // Execute the thunk
    },
  }
);

// Backward compatible wrapper for makeTransition
export const makeTransition = (txId, transitionName, params) => dispatch => {
  return dispatch(makeTransitionThunk({ txId, transitionName, params }));
};

////////////////////
// Fetch Messages //
////////////////////
const fetchMessagesPayloadCreator = (
  { txId, page, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > 0 && page > 1) {
        // Background update for new incoming messages
        dispatch(fetchMessagesThunk({ txId, page: 1, config })).catch(() => {
          // Background update, no need to to do anything atm.
        });
      }

      return { messages, pagination };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchMessagesThunk = createAsyncThunk(
  'TransactionPage/fetchMessages',
  fetchMessagesPayloadCreator
);

// Backward compatible wrapper for fetchMessages
export const fetchMessages = (txId, page, config) => dispatch => {
  return dispatch(fetchMessagesThunk({ txId, page, config }));
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessagesThunk({ txId, page: nextPage, config }));
};

/////////////////
// sendMessage //
/////////////////
const sendMessagePayloadCreator = (
  { txId, message, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      return dispatch(fetchMessagesThunk({ txId, page: 1, config }))
        .then(() => messageId)
        .catch(() => messageId);
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const sendMessageThunk = createAsyncThunk(
  'TransactionPage/sendMessage',
  sendMessagePayloadCreator
);

// Backward compatible wrapper for sendMessage
export const sendMessage = (txId, message, config) => dispatch => {
  return dispatch(sendMessageThunk({ txId, message, config }));
};

////////////////
// sendReview //
////////////////
const sendReviewPayloadCreator = (
  { tx, transitionOptionsInfo, params, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  const include = REVIEW_TX_INCLUDES;

  const sendReviewAsSecond = () => {
    return sdk.transactions
      .transition(
        { id: tx?.id, transition: reviewAsSecond, params },
        { expand: true, include, ...getImageVariants(config.layout.listingImage) }
      )
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  };

  const sendReviewAsFirst = () => {
    return sdk.transactions
      .transition(
        { id: tx?.id, transition: reviewAsFirst, params },
        { expand: true, include, ...getImageVariants(config.layout.listingImage) }
      )
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        // If transaction transition is invalid, lets try another endpoint.
        if (isTransactionsTransitionInvalidTransition(e)) {
          return sendReviewAsSecond();
        } else {
          return rejectWithValue(storableError(e));
        }
      });
  };

  return hasOtherPartyReviewedFirst ? sendReviewAsSecond() : sendReviewAsFirst();
};

export const sendReviewThunk = createAsyncThunk(
  'TransactionPage/sendReview',
  sendReviewPayloadCreator
);

// Backward compatible wrapper for sendReview
export const sendReview = (tx, transitionOptionsInfo, params, config) => dispatch => {
  return dispatch(sendReviewThunk({ tx, transitionOptionsInfo, params, config }));
};

//////////////////////
// fetchTransitions //
//////////////////////
const fetchTransitionsPayloadCreator = ({ id }, { rejectWithValue, extra: sdk }) => {
  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => res.data.data)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransitionsThunk = createAsyncThunk(
  'TransactionPage/fetchTransitions',
  fetchTransitionsPayloadCreator
);

// Backward compatible wrapper for fetchTransitions
export const fetchNextTransitions = id => dispatch => {
  return dispatch(fetchTransitionsThunk({ id }));
};

////////////////////
// FetchLineItems //
////////////////////
const fetchLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => response.data)
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchLineItemsThunk = createAsyncThunk(
  'TransactionPage/fetchLineItems',
  fetchLineItemsPayloadCreator
);

// Backward compatible wrapper for fetchLineItems
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchLineItemsThunk({ orderData, listingId, isOwnListing }));
};

// ================ Next-Steps Providers ================ //

const haversineDistanceMiles = (lat1, lng1, lat2, lng2) => {
  const R = 3958.8;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const PROVIDER_LISTING_FIELDS = {
  include: ['author', 'author.profileImage'],
  'fields.listing': [
    'title',
    'geolocation',
    'publicData.service_radius',
    'publicData.service_area',
  ],
  'fields.user': ['profile.displayName'],
  'fields.image': ['variants.square-small', 'variants.square-xsmall'],
};

const mapProviderListing = (listing, boatLat, boatLng) => {
  const { title, geolocation, publicData = {} } = listing.attributes;
  const { service_radius = 50, service_area = '' } = publicData;
  const author = listing.author || null;
  const profileImage = author?.profileImage || null;
  const slug = title
    ? title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
    : 'listing';
  const distance =
    geolocation && boatLat != null
      ? Math.round(
          haversineDistanceMiles(boatLat, boatLng, geolocation.lat, geolocation.lng) * 10
        ) / 10
      : null;
  return {
    id: listing.id.uuid,
    title,
    slug,
    authorName: author?.attributes?.profile?.displayName || '',
    profileImageUrl:
      profileImage?.attributes?.variants?.['square-small']?.url ||
      profileImage?.attributes?.variants?.['square-xsmall']?.url ||
      null,
    serviceArea: service_area,
    serviceRadius: service_radius,
    distance,
  };
};

// Fetch specific listings by UUID array, return mapped provider objects.
const fetchListingsByIds = async (sdk, ids, boatLat, boatLng) => {
  if (!ids || ids.length === 0) return [];
  const response = await sdk.listings.query({
    ids,
    ...PROVIDER_LISTING_FIELDS,
  });
  return denormalisedResponseEntities(response).map(l => mapProviderListing(l, boatLat, boatLng));
};

// Fetch listings by category proximity filter.
const fetchProximityListings = async (sdk, categoryLevel1, boatLat, boatLng) => {
  const response = await sdk.listings.query({
    pub_categoryLevel1: categoryLevel1,
    perPage: 100,
    page: 1,
    minStock: 1,
    stockMode: 'match-undefined',
    ...PROVIDER_LISTING_FIELDS,
  });
  return denormalisedResponseEntities(response)
    .map(l => mapProviderListing(l, boatLat, boatLng))
    .filter(p => p.distance !== null && p.distance <= p.serviceRadius)
    .sort((a, b) => a.distance - b.distance);
};

// ================ Signed Documents Status (Push 8 Phase A.4e-1a) ================ //
//
// Fetches the per-link signing status (Pending / Complete / Expired) for the
// customer-side Documents subsection on the booking confirmation page. The
// server endpoint is customer-scoped (403s providers), so this thunk only
// gets called for the customer's view of the transaction. Voided links are
// filtered server-side; the client never sees revoked rows.
//
// Returns an array of `{ linkId, submissionCount, hasSignedSubmission,
// lastSignedAt }` rows — one per non-voided signing link on the transaction.
// Empty array is a valid response (no links auto-attached).
// Push 9 Sub-phase C.2b-ii — renamed from fetchSignedDocumentsStatusThunk.
// The customer-signed-documents endpoint was reshaped from flat
// aggregate `signedDocuments[]` to nested `signingLinks[].submissions[]`
// (mirrors provider-signed-documents endpoint). The slice/thunk/state-key
// rename keeps the codebase readable. Provider-side
// fetchProviderSignedDocumentsStatusThunk is a separate concern and is
// not renamed.
export const fetchSigningLinksThunk = createAsyncThunk(
  'TransactionPage/fetchSigningLinks',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const resp = await customerSignedDocuments({ transactionId });
      return Array.isArray(resp?.signingLinks) ? resp.signingLinks : [];
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// Backward-compat wrapper matching the legacy `dispatch(fetchX(arg))` shape
// some loadData orchestrators use elsewhere in the codebase.
export const fetchSigningLinks = transactionId => dispatch =>
  dispatch(fetchSigningLinksThunk({ transactionId }));

// ================ Provider Signed Documents Status (Push 8 Phase A.4e-2a) ================ //
//
// Provider-side counterpart to fetchSigningLinksThunk. Same transactionId
// arg, different api wrapper (providerSignedDocuments vs
// customerSignedDocuments). Push 9 Sub-phase C.2b-ii: the customer endpoint
// was reshaped to return the same nested `{ signingLinks }` shape as the
// provider endpoint — privacy split lives server-side (customer's response
// returns submissions[] only for inspection links; null for non-inspection
// to preserve guest-signer privacy). Dispatched in loadData ONLY when
// params.transactionRole === 'provider' — saves an endpoint call on the
// customer view (which would 403 the provider endpoint anyway).
export const fetchProviderSignedDocumentsStatusThunk = createAsyncThunk(
  'TransactionPage/fetchProviderSignedDocumentsStatus',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const resp = await providerSignedDocuments({ transactionId });
      return Array.isArray(resp?.signingLinks) ? resp.signingLinks : [];
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// ================ Inspection Signing-Links Status (Push 9 Sub-phase B) ================ //
//
// Returns inspection signing-links for the transaction (party_role IS NOT
// NULL). Dispatched in loadData for BOTH provider and customer roles —
// the customer's TransactionPage uses the result to render the Cancel-slot
// wrapper (Check-In / Check-Out / Completed-at-dock); the provider's
// TransactionPanel uses it to render the InspectionButtons component.
//
// The signing_token field is filtered server-side per row-ownership: the
// customer sees their own row's token, the provider sees both their own
// row's token AND the customer-row's token (when the provider initiated
// the override). The client never has to reason about token visibility.
export const fetchInspectionSigningStatusThunk = createAsyncThunk(
  'TransactionPage/fetchInspectionSigningStatus',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const resp = await inspectionSigningStatus({ transactionId });
      // Server returns both the existing signing-link rows AND the
      // resolvable templates per document_type for the transaction's
      // listing. The client uses availableInspectionTemplates as a
      // fallback when no signing-link row exists yet (first-click case).
      return {
        inspectionSigningLinks: Array.isArray(resp?.inspectionSigningLinks)
          ? resp.inspectionSigningLinks
          : [],
        availableInspectionTemplates: resp?.availableInspectionTemplates || {
          inspection_pre_departure: null,
          inspection_post_return: null,
        },
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const fetchInspectionSigningStatus = transactionId => dispatch =>
  dispatch(fetchInspectionSigningStatusThunk({ transactionId }));

// ================ Start Inspection Signing (Push 9 Sub-phase B) ================ //
//
// Fired when provider clicks Check-In / Check-Out on TransactionPanel, or
// customer clicks Check-In / Check-Out in the TransactionPage Cancel slot.
// On 200, the caller navigates to the returned signing URL. On 409
// ('inspection_already_started'), the caller navigates to the existing
// link's URL — the race-lost handler is in the client (server returns
// the existing link in the 409 payload).
//
// After a successful start, the thunk re-fetches inspectionSigningStatus
// so the panel + Cancel-slot rerender with the updated state (a Party B
// initiation also flips Party A's counterpartEngagedAt server-side; the
// refetch surfaces that).
export const startInspectionSigningThunk = createAsyncThunk(
  'TransactionPage/startInspectionSigning',
  async ({ transactionId, templateId, partyRole }, { dispatch, rejectWithValue }) => {
    try {
      const resp = await startInspectionSigning({ transactionId, templateId, partyRole });
      // Refresh inspectionSigningLinks so the UI reflects the new row +
      // any counterpart-engaged flip.
      await dispatch(fetchInspectionSigningStatusThunk({ transactionId }));
      return { ...resp, partyRole };
    } catch (e) {
      // 409 'inspection_already_started' carries the existing-row payload
      // at the response root: { error: 'inspection_already_started',
      // link: { url, ... } }. src/util/api.js builds the thrown Error by
      // Object.assign'ing the response body onto a `new Error()`, so the
      // `link` field is present directly on `e`. storableError(e) only
      // preserves data.errors[], not root-level fields — without an
      // explicit extract step here, the existing-link URL is silently
      // dropped before the handler's catch sees it. Resolve raceLost so
      // the caller can navigate to the existing URL without going through
      // the rejection path. Refresh the status feed first so the UI sees
      // the existing row (it may have been created in another tab).
      const isInspectionAlreadyStarted = e?.error === 'inspection_already_started' && e?.link?.url;
      if (isInspectionAlreadyStarted) {
        await dispatch(fetchInspectionSigningStatusThunk({ transactionId }));
        return { raceLost: true, url: e.link.url, partyRole };
      }
      return rejectWithValue(storableError(e));
    }
  }
);

export const startInspection = ({ transactionId, templateId, partyRole }) => dispatch =>
  dispatch(startInspectionSigningThunk({ transactionId, templateId, partyRole })).unwrap();

// ================ Signed Document Detail (Push 8 Phase A.4e-1b / A.4e-2a) ================ //
//
// Lazy-fetched on the View click for one signing link's signed submission.
// Shared between customer-side and provider-side per A.4e-2a's Q-A4e2-1
// (same modal, same response shape, same state slice — only the api
// wrapper switches based on `isProvider`).
//
//   Customer side (A.4e-1b): id is a linkId, server selects the caller's
//   own submission via signer match. 404 if the customer never personally
//   signed (guest submissions are NOT shown under "your document").
//
//   Provider side (A.4e-2a): id is a submissionId (the row PK), server
//   auth-checks the caller is the provider on the submission's
//   transaction. No signer-match filter — provider sees ANY submission
//   tied to their booking.
//
// The id is intentionally stored in Redux state alongside the detail
// payload + an isProvider discriminator. The Retry handler then doesn't
// need to know which path is active — it reads both from state. This also
// avoids the class-vs-functional state mismatch in TransactionPanel
// (class component) vs the prompt's React.useState pattern.
export const fetchSignedDocumentDetailThunk = createAsyncThunk(
  'TransactionPage/fetchSignedDocumentDetail',
  async ({ id, isProvider }, { rejectWithValue }) => {
    try {
      const resp = isProvider
        ? await providerSignedDocumentDetail({ submissionId: id })
        : await customerSignedDocumentDetail({ linkId: id });
      return resp; // { pendingSignature, auditLog, template, transaction }
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const fetchNextStepsProvidersThunk = createAsyncThunk(
  'TransactionPage/fetchNextStepsProviders',
  async ({ listing }, { rejectWithValue, extra: sdk }) => {
    const publicData = listing?.attributes?.publicData || {};
    const geolocation = listing?.attributes?.geolocation;
    const boatLat = geolocation?.lat ?? null;
    const boatLng = geolocation?.lng ?? null;

    const captainRequirementsArr = Array.isArray(publicData.hired_captain_crew_requirements)
      ? publicData.hired_captain_crew_requirements
      : [];
    const pickupOptions = Array.isArray(publicData.pickup_options) ? publicData.pickup_options : [];
    const recommendedCaptainIds = Array.isArray(publicData.recommended_captains)
      ? publicData.recommended_captains.filter(v => typeof v === 'string')
      : [];
    const recommendedDeliveryIds = Array.isArray(publicData.recommended_delivery_providers)
      ? publicData.recommended_delivery_providers.filter(v => typeof v === 'string')
      : [];

    const showDelivery = pickupOptions.includes('third-party-ok');

    try {
      // ── Captains ──────────────────────────────────────────────────────────
      let captains = [];
      let captainsFallback = false;

      const captainRequired = captainRequirementsArr.includes('captain-required-any');
      const captainOptional = captainRequirementsArr.includes('captain-optional-preapproved');

      if (captainRequired || captainOptional) {
        if (recommendedCaptainIds.length > 0) {
          captains = await fetchListingsByIds(sdk, recommendedCaptainIds, boatLat, boatLng);
        } else {
          captains = await fetchProximityListings(sdk, 'captains', boatLat, boatLng);
          captainsFallback = true;
        }
      } else {
        // 'no-requirements' or unset — show proximity results
        captains = await fetchProximityListings(sdk, 'captains', boatLat, boatLng);
      }

      // ── Delivery providers ─────────────────────────────────────────────────
      let delivery = [];
      let deliveryFallback = false;

      if (showDelivery) {
        if (recommendedDeliveryIds.length > 0) {
          delivery = await fetchListingsByIds(sdk, recommendedDeliveryIds, boatLat, boatLng);
        } else {
          delivery = await fetchProximityListings(sdk, 'dock-ramp-delivery', boatLat, boatLng);
          deliveryFallback = true;
        }
      }

      return {
        captains,
        captainsFallback,
        showDelivery,
        delivery,
        deliveryFallback,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// ================ Slice ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  nextStepsProviders: null,
  nextStepsProvidersLoading: false,
  nextStepsProvidersError: null,
  // Push 8 Phase A.4e-1a — customer-side Documents subsection status feed.
  // Array of { linkId, submissionCount, hasSignedSubmission, lastSignedAt }
  // once fetched; null before fetch (used to distinguish "not yet loaded"
  // from "empty result" in the UI's loading vs empty-state branch).
  // Push 9 Sub-phase C.2b-ii — renamed from signedDocumentsStatus to
  // match the reshaped endpoint response (nested signingLinks[] per
  // provider-side parity).
  signingLinks: null,
  signingLinksInProgress: false,
  signingLinksError: null,
  // Push 8 Phase A.4e-1b / A.4e-2a — View modal lazy detail fetch (shared
  // between customer + provider sides per Q-A4e2-1). Same modal, same
  // response shape, same slice. Discriminator `signedDocumentDetailIsProvider`
  // tracks which path opened it so the Retry handler can pick the right
  // api wrapper. `signedDocumentDetailLinkId` is reused as a generic "id
  // of what to view" (linkId on customer side; submissionId on provider
  // side) — historical naming retained to avoid a rename touching every
  // mapStateToProps site.
  signedDocumentDetail: null,
  signedDocumentDetailInProgress: false,
  signedDocumentDetailError: null,
  signedDocumentDetailModalOpen: false,
  signedDocumentDetailLinkId: null,
  signedDocumentDetailIsProvider: false,
  // Push 8 Phase A.4e-2a — provider-side Documents section list feed.
  // Array of `{ linkId, templateId, templateName, documentType, signerRole,
  // signingUrl, expiresAt, submissions: [...] }` once fetched; null
  // before fetch (distinguishes "not yet loaded" from "empty result"
  // in the UI's loading-vs-empty-state branch).
  providerSignedDocumentsStatus: null,
  providerSignedDocumentsStatusInProgress: false,
  providerSignedDocumentsStatusError: null,

  // Push 9 Sub-phase B — inspection signing-link status feed. Populated by
  // fetchInspectionSigningStatusThunk in loadData. Empty array (NOT null)
  // is the explicit "no inspections started yet" sentinel — the UI relies
  // on this to render the "Start Check-In" button in not_started state.
  inspectionSigningLinks: [],
  inspectionSigningLinksInProgress: false,
  inspectionSigningLinksError: null,
  // Push 9 Sub-phase B — per-listing inspection template resolution map.
  // Populated by fetchInspectionSigningStatusThunk alongside the signing
  // links. Used by the client to resolve templateId when no signing-link
  // row exists yet (first-click case on Start Check-in / Start Check-out).
  // Server applies the Push 8 A.3.2 listing-mapping convention
  // (empty listing_ids = "all listings"; populated = explicit allow-list)
  // so the client just consumes the resolved IDs.
  availableInspectionTemplates: {
    inspection_pre_departure: null,
    inspection_post_return: null,
  },

  // Push 9 Sub-phase B — start-inspection in-flight type tracking. Set to
  // 'inspection_pre_departure' or 'inspection_post_return' while a
  // start-inspection-signing call is pending; null otherwise. Used by
  // InspectionButtons to disable both buttons while one is in-flight,
  // and by the customer-side Cancel-slot wrapper.
  inspectionInProgressType: null,
  inspectionStartError: null,
  // Push 8 Phase A.4f — MergeFailureCompletionModal state.
  //   modalOpen: drives the Modal mount via TransactionPanel
  //   linkId/templateName: identifies which template the modal is acting on
  //   fields: { templateName, missingFieldKeys, currentOverrides,
  //     guestEmails } once fetched
  //   *InProgress / *Error: per-thunk lifecycle for the three async ops
  //     (fetch fields, save overrides, send for signing)
  //   successState: { signingUrl, guestEmails } populated on send success
  //     — drives the confirmation-card view inside the modal
  mergeFailureCompletionModalOpen: false,
  mergeFailureCompletionLinkId: null,
  mergeFailureCompletionTemplateName: null,
  mergeFailureCompletionFields: null,
  mergeFailureCompletionFieldsInProgress: false,
  mergeFailureCompletionFieldsError: null,
  mergeFailureCompletionSaving: false,
  mergeFailureCompletionSavingError: null,
  mergeFailureCompletionSendingForSigning: false,
  mergeFailureCompletionSendingError: null,
  mergeFailureCompletionSendingMissingFieldKeys: null,
  mergeFailureCompletionSuccessState: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

const transactionPageSlice = createSlice({
  name: 'TransactionPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
    // Push 8 Phase A.4e-1b / A.4e-2a — modal-open / id tracking.
    // Synchronous reducer so the modal can flip open immediately on click
    // while the fetch thunk dispatches in parallel.
    //
    // Accepted payload shapes:
    //   - { id, isProvider }  — canonical shape (A.4e-2a onward)
    //   - string              — legacy customer-side shape (raw linkId);
    //                           isProvider defaults to false
    openSignedDocumentDetailModal: (state, action) => {
      const payload = action.payload;
      const id = typeof payload === 'string' ? payload : payload?.id || null;
      const isProvider = typeof payload === 'string' ? false : !!payload?.isProvider;
      state.signedDocumentDetailModalOpen = true;
      state.signedDocumentDetailLinkId = id;
      state.signedDocumentDetailIsProvider = isProvider;
      state.signedDocumentDetailError = null;
    },
    closeSignedDocumentDetailModal: state => {
      state.signedDocumentDetailModalOpen = false;
      state.signedDocumentDetail = null;
      state.signedDocumentDetailError = null;
      state.signedDocumentDetailLinkId = null;
      state.signedDocumentDetailIsProvider = false;
      // Leave signedDocumentDetailInProgress alone — fetch may still be
      // in flight; its settled state will eventually update.
    },
    // ── A.4f MergeFailureCompletionModal synchronous reducers ────────────
    openMergeFailureCompletionModal: (state, action) => {
      const { signingLinkId, templateName } = action.payload || {};
      state.mergeFailureCompletionModalOpen = true;
      state.mergeFailureCompletionLinkId = signingLinkId || null;
      state.mergeFailureCompletionTemplateName = templateName || null;
      // Clear stale state from any prior modal open. Fields refetched
      // by the open thunk; success state cleared so a re-open after a
      // successful send shows the form again (fresh detection cycle).
      state.mergeFailureCompletionFields = null;
      state.mergeFailureCompletionFieldsError = null;
      state.mergeFailureCompletionSavingError = null;
      state.mergeFailureCompletionSendingError = null;
      state.mergeFailureCompletionSendingMissingFieldKeys = null;
      state.mergeFailureCompletionSuccessState = null;
    },
    closeMergeFailureCompletionModal: state => {
      state.mergeFailureCompletionModalOpen = false;
      state.mergeFailureCompletionLinkId = null;
      state.mergeFailureCompletionTemplateName = null;
      state.mergeFailureCompletionFields = null;
      state.mergeFailureCompletionFieldsError = null;
      state.mergeFailureCompletionSavingError = null;
      state.mergeFailureCompletionSendingError = null;
      state.mergeFailureCompletionSendingMissingFieldKeys = null;
      state.mergeFailureCompletionSuccessState = null;
      // Leave in-flight flags alone — settled actions will clear them.
    },
  },
  extraReducers: builder => {
    builder
      // fetchTransaction cases
      .addCase(fetchTransactionThunk.pending, state => {
        state.fetchTransactionInProgress = true;
        state.fetchTransactionError = null;
      })
      .addCase(fetchTransactionThunk.fulfilled, (state, action) => {
        state.fetchTransactionInProgress = false;
        state.transactionRef = { id: action.payload.data.data.id, type: 'transaction' };
      })
      .addCase(fetchTransactionThunk.rejected, (state, action) => {
        state.fetchTransactionInProgress = false;
        state.fetchTransactionError = action.payload;
      })
      // fetchTransitions cases
      .addCase(fetchTransitionsThunk.pending, state => {
        state.fetchTransitionsInProgress = true;
        state.fetchTransitionsError = null;
      })
      .addCase(fetchTransitionsThunk.fulfilled, (state, action) => {
        state.fetchTransitionsInProgress = false;
        state.processTransitions = action.payload;
      })
      .addCase(fetchTransitionsThunk.rejected, (state, action) => {
        state.fetchTransitionsInProgress = false;
        state.fetchTransitionsError = action.payload;
      })
      // makeTransition cases
      .addCase(makeTransitionThunk.pending, (state, action) => {
        state.transitionInProgress = action.meta.arg.transitionName;
        state.transitionError = null;
      })
      .addCase(makeTransitionThunk.fulfilled, state => {
        state.transitionInProgress = null;
      })
      .addCase(makeTransitionThunk.rejected, (state, action) => {
        state.transitionInProgress = null;
        state.transitionError = action.payload;
      })
      // fetchMessages cases
      .addCase(fetchMessagesThunk.pending, state => {
        state.fetchMessagesInProgress = true;
        state.fetchMessagesError = null;
      })
      .addCase(fetchMessagesThunk.fulfilled, (state, action) => {
        const { messages, pagination } = action.payload;
        const oldestMessagePageFetched =
          state.oldestMessagePageFetched > pagination.page
            ? state.oldestMessagePageFetched
            : pagination.page;

        state.fetchMessagesInProgress = false;
        state.messages = mergeEntityArrays(state.messages, messages);
        state.totalMessages = pagination.totalItems;
        state.totalMessagePages = pagination.totalPages;
        state.oldestMessagePageFetched = oldestMessagePageFetched;
      })
      .addCase(fetchMessagesThunk.rejected, (state, action) => {
        state.fetchMessagesInProgress = false;
        state.fetchMessagesError = action.payload;
      })
      // sendMessage cases
      .addCase(sendMessageThunk.pending, state => {
        state.sendMessageInProgress = true;
        state.sendMessageError = null;
      })
      .addCase(sendMessageThunk.fulfilled, state => {
        state.sendMessageInProgress = false;
      })
      .addCase(sendMessageThunk.rejected, (state, action) => {
        state.sendMessageInProgress = false;
        state.sendMessageError = action.payload;
      })
      // sendReview cases
      .addCase(sendReviewThunk.pending, state => {
        state.sendReviewInProgress = true;
        state.sendReviewError = null;
      })
      .addCase(sendReviewThunk.fulfilled, state => {
        state.sendReviewInProgress = false;
      })
      .addCase(sendReviewThunk.rejected, (state, action) => {
        state.sendReviewInProgress = false;
        state.sendReviewError = action.payload;
      })
      // fetchLineItems cases
      .addCase(fetchLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      })
      // fetchTimeSlots cases
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { timeZone, options } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(action.meta.arg.start, timeZone);
          state.timeSlotsForDate = {
            ...removeOutdatedDateData(state.timeSlotsForDate),
            [dateId]: {
              ...state.timeSlotsForDate[dateId],
              fetchTimeSlotsError: null,
              fetchedAt: null,
              fetchTimeSlotsInProgress: true,
              timeSlots: [],
            },
          };
        } else {
          const monthId = monthIdString(action.meta.arg.start, timeZone);
          state.monthlyTimeSlots[monthId] = {
            ...state.monthlyTimeSlots[monthId],
            fetchTimeSlotsError: null,
            fetchTimeSlotsInProgress: true,
          };
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { timeSlots, dateId, monthId, type } = action.payload;

        if (type === 'date') {
          state.timeSlotsForDate[dateId] = {
            ...state.timeSlotsForDate[dateId],
            fetchTimeSlotsInProgress: false,
            fetchedAt: new Date().getTime(),
            timeSlots,
          };
        } else if (type === 'month') {
          state.monthlyTimeSlots[monthId] = {
            ...state.monthlyTimeSlots[monthId],
            fetchTimeSlotsInProgress: false,
            timeSlots,
          };
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { error, dateId, monthId, type } = action.payload;

        if (type === 'date') {
          state.timeSlotsForDate[dateId] = {
            ...state.timeSlotsForDate[dateId],
            fetchTimeSlotsInProgress: false,
            fetchTimeSlotsError: error,
          };
        } else if (type === 'month') {
          state.monthlyTimeSlots[monthId] = {
            ...state.monthlyTimeSlots[monthId],
            fetchTimeSlotsInProgress: false,
            fetchTimeSlotsError: error,
          };
        }
      })
      // fetchNextStepsProviders cases
      .addCase(fetchNextStepsProvidersThunk.pending, state => {
        state.nextStepsProvidersLoading = true;
        state.nextStepsProvidersError = null;
      })
      .addCase(fetchNextStepsProvidersThunk.fulfilled, (state, action) => {
        state.nextStepsProvidersLoading = false;
        state.nextStepsProviders = action.payload;
      })
      .addCase(fetchNextStepsProvidersThunk.rejected, (state, action) => {
        state.nextStepsProvidersLoading = false;
        state.nextStepsProvidersError = action.payload;
      })
      // fetchSigningLinks cases (Push 8 Phase A.4e-1a + Push 9 Sub-phase
      // C.2b-ii rename)
      .addCase(fetchSigningLinksThunk.pending, state => {
        state.signingLinksInProgress = true;
        state.signingLinksError = null;
      })
      .addCase(fetchSigningLinksThunk.fulfilled, (state, action) => {
        state.signingLinksInProgress = false;
        state.signingLinks = action.payload;
      })
      .addCase(fetchSigningLinksThunk.rejected, (state, action) => {
        state.signingLinksInProgress = false;
        state.signingLinksError = action.payload || action.error;
      })
      // fetchSignedDocumentDetail cases (Push 8 Phase A.4e-1b / A.4e-2a)
      .addCase(fetchSignedDocumentDetailThunk.pending, state => {
        state.signedDocumentDetailInProgress = true;
        state.signedDocumentDetailError = null;
      })
      .addCase(fetchSignedDocumentDetailThunk.fulfilled, (state, action) => {
        state.signedDocumentDetailInProgress = false;
        state.signedDocumentDetail = action.payload;
      })
      .addCase(fetchSignedDocumentDetailThunk.rejected, (state, action) => {
        state.signedDocumentDetailInProgress = false;
        state.signedDocumentDetailError = action.payload || action.error;
      })
      // fetchProviderSignedDocumentsStatus cases (Push 8 Phase A.4e-2a)
      .addCase(fetchProviderSignedDocumentsStatusThunk.pending, state => {
        state.providerSignedDocumentsStatusInProgress = true;
        state.providerSignedDocumentsStatusError = null;
      })
      .addCase(fetchProviderSignedDocumentsStatusThunk.fulfilled, (state, action) => {
        state.providerSignedDocumentsStatusInProgress = false;
        state.providerSignedDocumentsStatus = action.payload;
      })
      .addCase(fetchProviderSignedDocumentsStatusThunk.rejected, (state, action) => {
        state.providerSignedDocumentsStatusInProgress = false;
        state.providerSignedDocumentsStatusError = action.payload || action.error;
      })
      // Push 9 Sub-phase B — inspection signing-link status + start thunks.
      .addCase(fetchInspectionSigningStatusThunk.pending, state => {
        state.inspectionSigningLinksInProgress = true;
        state.inspectionSigningLinksError = null;
      })
      .addCase(fetchInspectionSigningStatusThunk.fulfilled, (state, action) => {
        state.inspectionSigningLinksInProgress = false;
        // Thunk now returns { inspectionSigningLinks, availableInspectionTemplates }.
        state.inspectionSigningLinks = action.payload?.inspectionSigningLinks || [];
        state.availableInspectionTemplates = action.payload?.availableInspectionTemplates || {
          inspection_pre_departure: null,
          inspection_post_return: null,
        };
      })
      .addCase(fetchInspectionSigningStatusThunk.rejected, (state, action) => {
        state.inspectionSigningLinksInProgress = false;
        state.inspectionSigningLinksError = action.payload || action.error;
      })
      .addCase(startInspectionSigningThunk.pending, (state, action) => {
        // Use the templateId-less {transactionId, partyRole, ...} arg to
        // derive the document_type for in-flight tracking. The component
        // passes the document_type as part of the arg too so we know
        // which button to disable. If absent (defensive), fall back to a
        // generic non-null sentinel — both buttons disable.
        const docType = action?.meta?.arg?.documentType || 'in_flight';
        state.inspectionInProgressType = docType;
        state.inspectionStartError = null;
      })
      .addCase(startInspectionSigningThunk.fulfilled, state => {
        state.inspectionInProgressType = null;
      })
      .addCase(startInspectionSigningThunk.rejected, (state, action) => {
        state.inspectionInProgressType = null;
        state.inspectionStartError = action.payload || action.error;
      })
      // A.4f — MergeFailureCompletionModal thunks ─────────────────────────
      .addCase(fetchMergeFailureFieldsThunk.pending, state => {
        state.mergeFailureCompletionFieldsInProgress = true;
        state.mergeFailureCompletionFieldsError = null;
      })
      .addCase(fetchMergeFailureFieldsThunk.fulfilled, (state, action) => {
        state.mergeFailureCompletionFieldsInProgress = false;
        state.mergeFailureCompletionFields = action.payload;
      })
      .addCase(fetchMergeFailureFieldsThunk.rejected, (state, action) => {
        state.mergeFailureCompletionFieldsInProgress = false;
        state.mergeFailureCompletionFieldsError = action.payload || action.error;
      })
      .addCase(saveMergeFailureOverridesThunk.pending, state => {
        state.mergeFailureCompletionSaving = true;
        state.mergeFailureCompletionSavingError = null;
      })
      .addCase(saveMergeFailureOverridesThunk.fulfilled, state => {
        state.mergeFailureCompletionSaving = false;
      })
      .addCase(saveMergeFailureOverridesThunk.rejected, (state, action) => {
        state.mergeFailureCompletionSaving = false;
        state.mergeFailureCompletionSavingError = action.payload || action.error;
      })
      .addCase(sendForSigningThunk.pending, state => {
        state.mergeFailureCompletionSendingForSigning = true;
        state.mergeFailureCompletionSendingError = null;
        state.mergeFailureCompletionSendingMissingFieldKeys = null;
      })
      .addCase(sendForSigningThunk.fulfilled, (state, action) => {
        state.mergeFailureCompletionSendingForSigning = false;
        state.mergeFailureCompletionSuccessState = {
          signingUrl: action.payload?.signingUrl || null,
          guestEmails: Array.isArray(action.payload?.guestEmails) ? action.payload.guestEmails : [],
          resolvedFailureCount: action.payload?.resolvedFailureCount || 0,
        };
      })
      .addCase(sendForSigningThunk.rejected, (state, action) => {
        state.mergeFailureCompletionSendingForSigning = false;
        // storableError preserves the underlying body — extract
        // missingFieldKeys if present (server's 400 payload). Modal
        // uses these to highlight affected inputs.
        const payload = action.payload || {};
        state.mergeFailureCompletionSendingError = payload;
        const detail = payload?.detail || payload;
        const candidate = detail?.missingFieldKeys || detail?.apiErrors?.[0]?.missingFieldKeys;
        state.mergeFailureCompletionSendingMissingFieldKeys = Array.isArray(candidate)
          ? candidate
          : null;
      });
  },
});

export const {
  setInitialValues,
  openSignedDocumentDetailModal,
  closeSignedDocumentDetailModal,
  // A.4f
  openMergeFailureCompletionModal,
  closeMergeFailureCompletionModal,
} = transactionPageSlice.actions;

// Push 8 Phase A.4e-1b / A.4e-2a — binding thunk action creators wired
// into mapDispatchToProps. onViewSignedDocument flips the modal open AND
// dispatches the lazy fetch; the modal renders its loading/error/loaded
// state machine from there. onRetry re-fetches using the id + isProvider
// flag stashed in state by openSignedDocumentDetailModal.

// Customer side: arg is a linkId from the customer's Documents subsection.
export const onViewSignedDocument = linkId => dispatch => {
  dispatch(openSignedDocumentDetailModal({ id: linkId, isProvider: false }));
  return dispatch(fetchSignedDocumentDetailThunk({ id: linkId, isProvider: false }));
};

// Provider side (A.4e-2a): arg is a submissionId from the provider's
// Documents section. Different api wrapper (provider-signed-document-detail
// instead of customer-...), same modal + state slice.
export const onViewSignedDocumentProvider = submissionId => dispatch => {
  dispatch(openSignedDocumentDetailModal({ id: submissionId, isProvider: true }));
  return dispatch(fetchSignedDocumentDetailThunk({ id: submissionId, isProvider: true }));
};

export const onCloseSignedDocumentDetailModal = () => dispatch => {
  dispatch(closeSignedDocumentDetailModal());
};

// ── Push 8 Phase A.4e-2b — lazy message-posting thunk ──────────────────
//
// Posts the merge-failure activity-feed message via the marketplace SDK
// (caller-as-sender pattern — same as sendMessage thunk above), then
// idempotently marks message_posted_at on the failure row via the
// /api/mark-merge-failure-message-posted endpoint.
//
// Copy variant is chosen by the caller: 'provider' (provider posting) or
// 'customer' (customer posting). Both variants are functionally
// equivalent ("a document failed; manual update needed"); the difference
// is voice — provider speaks in first person ("I'll update"), customer
// describes ("the provider needs to update"). Either side's variant is
// acceptable for the activity feed.
//
// Race condition: if provider + customer load the transaction page
// simultaneously, both may attempt to post. Server-side
// mark-merge-failure-message-posted is idempotent (no-op when already
// stamped); two activity-feed messages may briefly appear, but no DB
// corruption.
//
// Caller usage: dispatch from TransactionPanel useEffect after a
// 1500ms delay. Only fires when mergeFailure exists + messagePostedAt
// IS NULL.
export const postMergeFailureMessageThunk = createAsyncThunk(
  'TransactionPage/postMergeFailureMessage',
  async ({ txId, failureId, variant, config }, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      // Resolve copy via injected translation key (handled at the caller
      // because intl access in thunks is non-trivial). variant is one of
      // 'provider' | 'customer'; messageContent is the resolved string.
      const messageContent = variant?.messageContent;
      if (!txId || !failureId || !messageContent) {
        return rejectWithValue({ code: 'INVALID_ARGS' });
      }
      await sdk.messages.send({ transactionId: txId, content: messageContent });
      // Stamp message_posted_at server-side. Idempotent on race.
      await markMergeFailureMessagePosted({ failureId });
      // Refresh message list so the new post shows in the activity feed.
      await dispatch(fetchMessagesThunk({ txId, page: 1, config })).catch(() => {});
      return { failureId };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[postMergeFailureMessage] send/mark failed:', e?.message);
      return rejectWithValue(storableError(e));
    }
  }
);

// Binding action creator — caller passes the already-resolved
// messageContent so intl translation happens at the React layer.
export const postMergeFailureMessage = (txId, failureId, variant, config) => dispatch => {
  return dispatch(postMergeFailureMessageThunk({ txId, failureId, variant, config }));
};

// Single retry binding for both customer + provider paths — reads the
// active id + isProvider discriminator from state. Modal's Retry button
// invokes this without arguments.
export const onRetrySignedDocumentDetail = () => (dispatch, getState) => {
  const { signedDocumentDetailLinkId, signedDocumentDetailIsProvider } = getState().TransactionPage;
  if (!signedDocumentDetailLinkId) return Promise.resolve();
  return dispatch(
    fetchSignedDocumentDetailThunk({
      id: signedDocumentDetailLinkId,
      isProvider: signedDocumentDetailIsProvider,
    })
  );
};

// ================ A.4f Merge-failure completion workflow ================ //

// fetchMergeFailureFields — modal hydration. Called on Update-button
// click. Returns { templateName, missingFieldKeys, currentOverrides,
// guestEmails } from /api/merge-failure-fields. Failure rejects with
// storableError; the modal renders its error state.
export const fetchMergeFailureFieldsThunk = createAsyncThunk(
  'TransactionPage/fetchMergeFailureFields',
  async ({ transactionId, signingLinkId }, { rejectWithValue }) => {
    try {
      const resp = await mergeFailureFields({ transactionId, signingLinkId });
      return resp;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// saveMergeFailureOverrides — autosave on form submit before Send for
// Signing. Idempotent (upserts each entry). Returns { savedCount }.
export const saveMergeFailureOverridesThunk = createAsyncThunk(
  'TransactionPage/saveMergeFailureOverrides',
  async ({ transactionId, overrides }, { rejectWithValue }) => {
    try {
      const resp = await saveMergeFailureOverrides({ transactionId, overrides });
      return resp;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// sendForSigning — final commit + customer notification. Modal renders
// the success-state confirmation card on resolve. On 400 with
// missingFieldKeys, modal re-highlights the affected inputs.
export const sendForSigningThunk = createAsyncThunk(
  'TransactionPage/sendForSigning',
  async ({ signingLinkId, messageContent, transactionId }, { dispatch, rejectWithValue }) => {
    try {
      const resp = await sendForSigning({ signingLinkId, messageContent });
      // Refresh the provider-signed-documents feed so the banner
      // updates (failure rows now have resolved_at set; the JOIN
      // filters them out). Fire-and-forget — modal already has the
      // success-state data it needs.
      if (transactionId) {
        dispatch(fetchProviderSignedDocumentsStatusThunk({ transactionId })).catch(() => {});
      }
      // Also refresh messages list so the just-posted activity-feed
      // entry surfaces in the customer's view if they happen to be
      // looking at the page.
      return resp;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// Binding action creators — wired into TransactionPage's
// mapDispatchToProps + threaded down to the banner + modal.
export const onOpenMergeFailureCompletionModal = ({
  transactionId,
  signingLinkId,
  templateName,
}) => dispatch => {
  dispatch(
    transactionPageSlice.actions.openMergeFailureCompletionModal({
      signingLinkId,
      templateName,
    })
  );
  return dispatch(fetchMergeFailureFieldsThunk({ transactionId, signingLinkId }));
};

export const onCloseMergeFailureCompletionModal = () => dispatch => {
  dispatch(transactionPageSlice.actions.closeMergeFailureCompletionModal());
};

export const onSaveOverrides = ({ transactionId, overrides }) => dispatch =>
  dispatch(saveMergeFailureOverridesThunk({ transactionId, overrides }));

export const onSendForSigning = ({ signingLinkId, messageContent, transactionId }) => dispatch =>
  dispatch(sendForSigningThunk({ signingLinkId, messageContent, transactionId }));

export default transactionPageSlice.reducer;

// ================ Load data ================ //

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  // Push 8 Phase A.4e-2a — symmetric conditional dispatch for signed-
  // documents feeds. The customer endpoint 403s providers and vice versa,
  // so dispatching the wrong side just wastes a roundtrip and clutters
  // Redux state with a rejected promise. params.transactionRole is set
  // by the /order/:id and /sale/:id loadData wrappers in
  // src/routing/routeConfiguration.js — both routes inject the role
  // explicitly into params so the duck can branch reliably. (The /sale
  // wrapper was added in A.4e-2a alongside this fix; before that it was
  // undefined at loadData time.)
  const isProviderView = txRole === 'provider';
  const isCustomerView = txRole === 'customer';

  // Sale / order (i.e. transaction entity in API)
  return Promise.all([
    dispatch(fetchTransactionThunk({ id: txId, txRole, config })),
    dispatch(fetchMessagesThunk({ txId, page: 1, config })),
    dispatch(fetchTransitionsThunk({ id: txId })),
    // Push 8 Phase A.4e-1a — customer-side Documents subsection status feed.
    // Customer view only; provider view would 403 the customer endpoint.
    isCustomerView
      ? dispatch(fetchSigningLinksThunk({ transactionId: params.id }))
      : Promise.resolve(),
    // Push 8 Phase A.4e-2a — provider-side Documents section status feed.
    // Provider view only; customer view would 403 the provider endpoint.
    isProviderView
      ? dispatch(fetchProviderSignedDocumentsStatusThunk({ transactionId: params.id }))
      : Promise.resolve(),
    // Push 9 Sub-phase B — inspection signing-link status. Fired for BOTH
    // provider and customer views — both surfaces (provider's
    // InspectionButtons + customer's Cancel-slot wrapper) read from this
    // feed. Server endpoint auth-gates by caller-is-a-party-to-the-tx;
    // signing_token is filtered server-side per row-ownership.
    isProviderView || isCustomerView
      ? dispatch(fetchInspectionSigningStatusThunk({ transactionId: params.id }))
      : Promise.resolve(),
  ]);
};
